import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import {
  ArrowLeft, CheckCircle2, Archive, Globe, Clock, Award, Trash2,
  BookOpen, Layers, GraduationCap, Calendar, Users, Coins, ExternalLink,
  Trophy, TrendingDown, BarChart3, Timer, Hourglass, Settings, RotateCcw, FileEdit,
  Folder, ChevronDown, ChevronRight, ChevronsUpDown, FileText, MoreHorizontal
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import { resolveBackNavigation } from '@/lib/utils'

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface TestQuestionItem {
  id: number
  test_question_id: number
  order_index: number
  section_name?: string | null
  section_id?: number | null
  preview: string | null
  difficulty: string
  question_type: string
  marks_positive: number
  marks_negative: number
  subject?: FilterOption | null
  chapter?: FilterOption | null
  section?: FilterOption | null
  standard?: FilterOption | null
  is_pyq?: boolean
  is_exemplar?: boolean
  pyq_info?: { exam?: string; year?: string | number } | null
  tags?: string[]
}

interface FilterOption { id: number | string; name: string; subject_id?: number }
interface AvailableExam { id: number; name: string }

interface TestDetail {
  id: number
  title: string
  description: string | null
  instructions_html?: string | null
  test_type: string
  difficulty_level?: string | null
  language: string
  status: string
  is_free: boolean
  credit_cost: number
  question_count: number
  total_marks: number
  duration_minutes: number | null
  positive_marks?: number | null
  negative_marks?: number | null
  negative_marking_enabled: boolean
  exam_id?: number | null
  exam: string | null
  created_by: string | null
  allow_multiple_attempts: boolean
  max_attempts: number | null
  mapped_exams?: FilterOption[]
  mapped_subjects?: FilterOption[]
  mapped_chapters?: FilterOption[]
  mapped_sections?: FilterOption[]
  mapped_standards?: FilterOption[]
  mapped_syllabi?: FilterOption[]
  syllabus_coverage?: {
    sections: { id: number; name: string }[]
    subjects: { id: number; name: string }[]
    chapters: { id: number; name: string }[]
    topics: string[]
  } | null
  attempts_count?: number
  unique_users_count?: number
  total_credits_generated?: number
  highest_score?: number
  lowest_score?: number
  average_score?: number
  average_percentage?: number
  avg_time_spent_seconds?: number
  total_time_spent_seconds?: number
  activated_at?: string
}

interface TestPaperSectionDetail {
  id: number
  name: string
  sort_order: number
  total_questions: number
  assigned_questions_count: number
  max_attempts: number | null
  duration_minutes: number | null
  subject_hint: string | null
  subject_name: string | null
  marking_rule: {
    id: number
    name: string
    scoring_strategy: string
    positive_marks: number
    negative_marks: number
  } | null
}

interface Props {
  test: TestDetail
  questions: TestQuestionItem[]
  test_paper_sections?: TestPaperSectionDetail[]
  available_exams?: AvailableExam[]
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0m'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60)
    const remMins = mins % 60
    return `${hrs}h ${remMins}m`
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

export default function TestsShow({ test, questions, test_paper_sections = [], available_exams = [] }: Props) {
  const { toast } = useToast()
  const [publishing, setPublishing] = useState(false)
  const [unpublishing, setUnpublishing] = useState(false)
  const [groupMode, setGroupMode] = useState<'section' | 'subject' | 'flat'>('section')

  const [collapsedPrimaries, setCollapsedPrimaries] = useState<Record<string, boolean>>({})
  const [collapsedSubjects, setCollapsedSubjects] = useState<Record<string, boolean>>({})
  const [collapsedChapters, setCollapsedChapters] = useState<Record<string, boolean>>({})
  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState<boolean>(true)
  const [expandedCoverageGroups, setExpandedCoverageGroups] = useState<Record<string, boolean>>({})

  const togglePrimary = (name: string) => {
    setCollapsedPrimaries(prev => ({ ...prev, [name]: !prev[name] }))
  }

  const toggleSubject = (key: string) => {
    setCollapsedSubjects(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleChapter = (key: string) => {
    setCollapsedChapters(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleAll = () => {
    const hasAnyCollapsed = Object.values(collapsedPrimaries).some(Boolean) ||
      Object.values(collapsedSubjects).some(Boolean) ||
      Object.values(collapsedChapters).some(Boolean)

    if (hasAnyCollapsed) {
      setCollapsedPrimaries({})
      setCollapsedSubjects({})
      setCollapsedChapters({})
    } else {
      const allPrim: Record<string, boolean> = {}
      const allSubs: Record<string, boolean> = {}
      const allChaps: Record<string, boolean> = {}

      if (groupMode === 'section' && test_paper_sections.length > 0) {
        test_paper_sections.forEach(sec => {
          allPrim[sec.name] = true
          const secQs = questions.filter(q => q.section_id === sec.id || q.section_name === sec.name)
          const subNames = Array.from(new Set(secQs.map(q => q.subject?.name || 'General Subject')))
          subNames.forEach(subName => {
            allSubs[`${sec.name}-${subName}`] = true
            const subQs = secQs.filter(q => (q.subject?.name || 'General Subject') === subName)
            const chapNames = Array.from(new Set(subQs.map(q => q.chapter?.name || 'General Topics')))
            chapNames.forEach(chName => {
              allChaps[`${sec.name}-${subName}-${chName}`] = true
            })
          })
        })
      } else {
        const subNames = Array.from(new Set(questions.map(q => q.subject?.name || 'General Subject')))
        subNames.forEach(subName => {
          allPrim[subName] = true
          const subQs = questions.filter(q => (q.subject?.name || 'General Subject') === subName)
          const chapNames = Array.from(new Set(subQs.map(q => q.chapter?.name || 'General Topics')))
          chapNames.forEach(chName => {
            allChaps[`${subName}-${chName}`] = true
          })
        })
      }

      setCollapsedPrimaries(allPrim)
      setCollapsedSubjects(allSubs)
      setCollapsedChapters(allChaps)
    }
  }

  // Edit Test Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false)
  const [editFormData, setEditFormData] = useState({
    title: test.title || '',
    description: test.description || '',
    exam_id: test.exam_id ? String(test.exam_id) : 'none',
    language: test.language || 'en',
    test_type: test.test_type || 'full_mock',
    duration_minutes: (test.duration_minutes ?? '') as number | '',
    is_free: test.is_free ?? true,
    credit_cost: (test.credit_cost || 0) as number | '',
    negative_marking_enabled: test.negative_marking_enabled ?? true,
    allow_multiple_attempts: test.allow_multiple_attempts ?? true,
  })

  const parseInputVal = (val: string) => (val === '' ? '' : Math.max(0, parseInt(val, 10) || 0))

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmittingEdit(true)

    const durVal = editFormData.duration_minutes === '' || editFormData.duration_minutes === null
      ? null
      : (Number(editFormData.duration_minutes) || null)

    const payload = {
      ...editFormData,
      exam_id: editFormData.exam_id === 'none' ? null : editFormData.exam_id,
      duration_minutes: durVal,
      credit_cost: editFormData.is_free ? 0 : (Number(editFormData.credit_cost) || 0),
    }

    router.patch(`/admin/tests/${test.id}`, { test: payload }, {
      onSuccess: () => {
        setIsEditModalOpen(false)
        toast({ title: 'Test configuration updated successfully!' })
      },
      onError: (errs) => {
        toast({ title: 'Failed to update test', description: Object.values(errs).join(', '), variant: 'destructive' })
      },
      onFinish: () => setIsSubmittingEdit(false),
    })
  }

  const handlePublish = () => {
    setPublishing(true)
    router.post(`/admin/tests/${test.id}/publish`, {}, {
      onSuccess: () => toast({ title: 'Test published!' }),
      onFinish: () => setPublishing(false),
    })
  }

  const handleUnpublish = () => {
    if ((test.attempts_count || 0) > 0) {
      toast({ title: 'Cannot unpublish test', description: 'Students have already submitted attempts for this test.', variant: 'destructive' })
      return
    }
    if (confirm('Unpublish this test and revert back to Draft status?')) {
      setUnpublishing(true)
      router.post(`/admin/tests/${test.id}/unpublish`, {}, {
        onSuccess: () => toast({ title: 'Test reverted to draft status' }),
        onFinish: () => setUnpublishing(false),
      })
    }
  }

  const handleArchive = () => {
    if (confirm('Are you sure you want to archive this test?')) {
      router.post(`/admin/tests/${test.id}/archive`, {}, {
        onSuccess: () => toast({ title: 'Test archived' }),
      })
    }
  }

  const handleRemoveQuestion = (questionId: number) => {
    if (test.status !== 'draft') {
      toast({ title: 'Cannot remove questions from a published test', variant: 'destructive' })
      return
    }
    if (confirm('Remove question from this test?')) {
      router.delete(`/admin/tests/${test.id}/questions/${questionId}`, {
        onSuccess: () => toast({ title: 'Question removed from test' }),
      })
    }
  }

  const { href: backHref, label: backLabel } = resolveBackNavigation('/admin/tests', 'Back to Tests')

  const currentPathWithSearch = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : `/admin/tests/${test.id}`

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Navigation & Header Actions */}
        {/* Unified Top Navigation & Action Header */}
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" asChild className="h-8 px-2 text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0">
            <Link href={backHref}>
              <ArrowLeft className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{backLabel}</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </Button>

          <div className="flex items-center gap-1.5 shrink-0">
            {test.status !== 'published' && (
              <Button asChild variant="outline" size="sm" className="h-8 px-2.5 gap-1.5 text-xs font-semibold">
                <Link href={`/admin/test_generator?test_id=${test.id}`}>
                  <FileEdit className="h-3.5 w-3.5 text-primary" />
                  <span className="hidden sm:inline">Edit in Generator</span>
                  <span className="sm:hidden">Edit</span>
                </Link>
              </Button>
            )}
            {test.status === 'draft' && (
              <Button
                size="sm"
                className="h-8 px-2.5 bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-xs gap-1.5"
                onClick={handlePublish}
                disabled={publishing}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span>Publish</span>
              </Button>
            )}

            {test.status !== 'archived' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" title="More options">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {test.status === 'published' && (test.attempts_count || 0) === 0 && (
                    <DropdownMenuItem
                      onClick={handleUnpublish}
                      disabled={unpublishing}
                      className="cursor-pointer text-xs flex items-center gap-2 text-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5 text-primary" />
                      <span>Unpublish Test</span>
                    </DropdownMenuItem>
                  )}
                  {test.status !== 'archived' && (
                    <DropdownMenuItem
                      onClick={handleArchive}
                      className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer text-xs flex items-center gap-2"
                    >
                      <Archive className="h-3.5 w-3.5" />
                      <span>Archive Test</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Banner */}
            <Card>
              <CardHeader className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <CardTitle className="text-xl sm:text-2xl font-bold text-foreground leading-tight break-words">
                      {test.title}
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {test.description || 'No description provided.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap self-start sm:self-auto shrink-0">
                    <Badge variant="outline" className="capitalize text-xs font-semibold">
                      {test.test_type.replace(/_/g, ' ')}
                    </Badge>
                    {test.difficulty_level && (
                      <Badge variant="secondary" className="capitalize text-xs font-semibold">
                        {test.difficulty_level}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs font-semibold uppercase flex items-center gap-1">
                      <Globe className="h-3 w-3 text-muted-foreground" />
                      <span>{test.language}</span>
                    </Badge>
                    <Badge variant={test.status === 'published' ? 'default' : 'outline'} className="capitalize text-xs font-bold">
                      {test.status}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {/* Combined Metadata Badges / Info Strip */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/60 border border-border/70 text-foreground font-medium">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground text-[11px]">Duration:</span>
                    <span className="font-bold text-foreground">
                      {test.duration_minutes ? `${test.duration_minutes} mins` : 'Untimed'}
                    </span>
                    {test_paper_sections.length > 0 && test_paper_sections.some(s => s.duration_minutes != null) && (
                      <span className="text-[10px] text-muted-foreground font-normal">
                        (Sectional)
                      </span>
                    )}
                  </div>

                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/60 border border-border/70 text-foreground font-medium">
                    <Award className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground text-[11px]">Marks:</span>
                    <span className="font-bold text-foreground">{test.total_marks ?? 0} pts</span>
                  </div>

                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/60 border border-border/70 text-foreground font-medium">
                    <Coins className="h-3.5 w-3.5 text-primary" />
                    <span className="text-muted-foreground text-[11px]">Access:</span>
                    {test.is_free ? (
                      <span className="font-bold text-foreground">Free</span>
                    ) : (
                      <span className="font-bold text-foreground">{test.credit_cost} credits</span>
                    )}
                  </div>
                </div>

                {test.instructions_html && (
                  <div className="mt-4 pt-3 border-t border-border/50 text-xs space-y-2">
                    <button
                      type="button"
                      onClick={() => setIsInstructionsCollapsed(prev => !prev)}
                      className="w-full flex items-center justify-between text-left group select-none hover:opacity-80 transition-opacity"
                    >
                      <div className="text-[11px] font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-primary" /> Candidate Instructions / Examiner Note
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
                        <span className="text-[10px]">{isInstructionsCollapsed ? 'Show' : 'Hide'}</span>
                        {isInstructionsCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </div>
                    </button>

                    {!isInstructionsCollapsed && (
                      <div
                        className="text-xs text-muted-foreground leading-relaxed bg-muted/30 p-3 rounded-xl border border-border/50 prose prose-xs dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: test.instructions_html }}
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Test Paper Sections Overview Banner (if sections configured) */}
            {test_paper_sections.length > 0 && (
              <div className="bg-card border border-border/80 rounded-2xl p-4 sm:p-5 shadow-glass-xs space-y-3.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 border-b border-border/60 pb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                    <Layers className="h-4 w-4 text-primary shrink-0" />
                    <span>Test Paper Sections & Marking Rules ({test_paper_sections.length})</span>
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25">
                      {!test.duration_minutes && !test_paper_sections.some(s => s.duration_minutes != null)
                        ? 'Untimed'
                        : test_paper_sections.some(s => s.duration_minutes != null)
                        ? 'Sectional Timers'
                        : 'Global Timer'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] font-bold bg-secondary/10 text-secondary border-secondary/25">
                      Structured Pattern
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {test_paper_sections.map(sec => (
                    <div key={sec.id} className="p-3.5 bg-muted/20 border border-border/60 rounded-xl space-y-2.5 text-xs flex flex-col justify-between">
                      {/* Full-width Section Title & Questions Count */}
                      <div className="space-y-1">
                        <div className="font-bold text-foreground text-xs leading-snug break-words">
                          {sec.name}
                        </div>
                        {sec.marking_rule && (
                          <div className="text-[11px] text-muted-foreground truncate font-medium">
                            Rule: {sec.marking_rule.name}
                          </div>
                        )}
                      </div>

                      {/* Footer Tags / Metrics */}
                      <div className="flex flex-wrap items-center justify-between gap-1.5 pt-2 border-t border-border/40 text-[11px]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline" className="text-[10px] font-mono font-bold bg-primary/10 text-primary border-primary/25 px-1.5 py-0 h-4">
                            {sec.assigned_questions_count} / {sec.total_questions} Qs
                          </Badge>
                          <span className="text-muted-foreground text-[10px]">
                            {sec.duration_minutes
                              ? `⏱ ${sec.duration_minutes}m`
                              : test.duration_minutes
                              ? `⏱ ${test.duration_minutes}m`
                              : 'Untimed'}
                          </span>
                        </div>
                        <span className="font-semibold text-foreground font-mono text-[11px]">
                          {sec.marking_rule ? `+${sec.marking_rule.positive_marks}/-${Math.abs(sec.marking_rule.negative_marks)}` : `+${test.positive_marks ?? 1}/-${test.negative_marks ?? 0}`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}        {/* Questions Table Container with Hierarchical Grouping Controls */}
        <Card>
          <CardHeader className="p-3.5 sm:p-5 flex flex-col gap-3 pb-3 border-b">
            <div>
              <CardTitle className="text-base sm:text-lg font-bold text-foreground">Questions in this Test ({questions.length})</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Review questions organized hierarchically by section, subject, and chapter
              </p>
            </div>

            {/* View Mode Controls & Toggle All combined */}
            <div className="flex items-center gap-1.5 w-full justify-between">
              <div className="flex items-center gap-1 border border-border/80 rounded-xl p-1 bg-muted/20 w-full">
                {groupMode !== 'flat' && questions.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="py-1 px-1.5 sm:px-2.5 text-[11px] sm:text-xs font-semibold rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all flex items-center gap-1 shrink-0"
                      title="Toggle Expand / Collapse All"
                    >
                      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0" />
                      <span className="hidden md:inline">Toggle All</span>
                    </button>
                    <div className="h-4 w-[1px] bg-border/80 mx-0.5 shrink-0" />
                  </>
                )}

                {test_paper_sections.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setGroupMode('section')}
                    className={`flex-1 py-1 px-1.5 sm:px-3 text-[11px] sm:text-xs font-bold rounded-lg text-center transition-all truncate ${
                      groupMode === 'section'
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Sections
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setGroupMode('subject')}
                  className={`flex-1 py-1 px-1.5 sm:px-3 text-[11px] sm:text-xs font-bold rounded-lg text-center transition-all truncate ${
                    groupMode === 'subject'
                      ? 'bg-primary text-primary-foreground shadow-2xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Subjects
                </button>
                <button
                  type="button"
                  onClick={() => setGroupMode('flat')}
                  className={`flex-1 py-1 px-1.5 sm:px-3 text-[11px] sm:text-xs font-bold rounded-lg text-center transition-all truncate ${
                    groupMode === 'flat'
                      ? 'bg-primary text-primary-foreground shadow-2xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Questions
                </button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-3 sm:p-4">
            {questions.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-xs">
                No questions assigned to this test yet.
              </div>
            ) : groupMode === 'section' && test_paper_sections.length > 0 ? (
              <div className="space-y-4">
                {test_paper_sections.map(sec => {
                  const secQuestions = questions.filter(q => q.section_id === sec.id || q.section_name === sec.name)
                  const isSecCollapsed = collapsedPrimaries[sec.name]
                  const secMarks = secQuestions.reduce((sum, q) => sum + (Number(q.marks_positive) || 1), 0)

                  // Group by Subject -> Chapter inside this section
                  const subjectNames = Array.from(new Set(secQuestions.map(q => q.subject?.name || 'General Subject')))

                  return (
                    <div
                      key={sec.id}
                      className="border border-border/80 rounded-2xl bg-card shadow-glass-xs overflow-hidden transition-all"
                    >
                      {/* Section Header */}
                      <div
                        onClick={() => togglePrimary(sec.name)}
                        className="p-3.5 bg-muted/40 hover:bg-muted/60 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 border-b border-border/60 transition-colors select-none"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-7 w-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Layers className="h-4 w-4" />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-foreground block">
                              {sec.name}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {subjectNames.length} {subjectNames.length === 1 ? 'Subject' : 'Subjects'}
                              {sec.duration_minutes ? ` • ${sec.duration_minutes} mins timer` : ''}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-2">
                          <Badge variant="outline" className="text-xs font-bold bg-primary/10 text-primary border-primary/25 px-2.5 py-0.5 rounded-lg flex items-center gap-1.5">
                            <span>{secQuestions.length} Questions</span>
                            {sec.marking_rule && (
                              <>
                                <span className="text-muted-foreground font-normal">•</span>
                                <span className="font-extrabold">{secMarks} Marks</span>
                              </>
                            )}
                          </Badge>
                          <div className="text-muted-foreground">
                            {isSecCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                        </div>
                      </div>

                      {/* Section Body: Nested Subject and Chapter accordions */}
                      {!isSecCollapsed && (
                        <div className="p-3 sm:p-3.5 space-y-3 bg-card">
                          {subjectNames.map(subjName => {
                            const subQs = secQuestions.filter(q => (q.subject?.name || 'General Subject') === subjName)
                            const subKey = `${sec.name}-${subjName}`
                            const isSubCollapsed = collapsedSubjects[subKey]
                            const chapNames = Array.from(new Set(subQs.map(q => q.chapter?.name || 'General Topics')))

                            return (
                              <div key={subjName} className="border border-border/70 rounded-xl overflow-hidden bg-background">
                                <div
                                  onClick={() => toggleSubject(subKey)}
                                  className="px-3.5 py-2.5 bg-muted/20 hover:bg-muted/40 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 transition-colors select-none"
                                >
                                  <div className="flex items-center gap-2">
                                    <BookOpen className="h-3.5 w-3.5 text-primary shrink-0" />
                                    <span className="text-xs font-bold text-foreground">
                                      {subjName}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between sm:justify-end gap-2">
                                    <Badge variant="outline" className="text-[10px] font-semibold bg-muted text-foreground border-border px-2 py-0 h-4 flex items-center gap-1">
                                      <span>{subQs.length} Questions</span>
                                    </Badge>
                                    <div className="text-muted-foreground">
                                      {isSubCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                    </div>
                                  </div>
                                </div>

                                {!isSubCollapsed && (
                                  <div className="p-2 sm:p-2.5 space-y-2.5 bg-muted/5">
                                    {chapNames.map(chapName => {
                                      const chapQs = subQs.filter(q => (q.chapter?.name || 'General Topics') === chapName)
                                      const chapKey = `${sec.name}-${subjName}-${chapName}`
                                      const isChapCollapsed = collapsedChapters[chapKey]

                                      return (
                                        <div key={chapName} className="border border-border/60 rounded-lg overflow-hidden bg-card">
                                          <div
                                            onClick={() => toggleChapter(chapKey)}
                                            className="px-3 py-2 bg-muted/15 hover:bg-muted/30 cursor-pointer flex items-center justify-between gap-2 border-b border-border/30 transition-colors select-none text-xs"
                                          >
                                            <div className="flex items-center gap-2 min-w-0">
                                              <Folder className="h-3 w-3 text-muted-foreground shrink-0" />
                                              <span className="font-semibold text-foreground text-[11px] truncate">
                                                {chapName}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-3.5 bg-muted text-muted-foreground border-border">
                                                {chapQs.length} Questions
                                              </Badge>
                                              <div className="text-muted-foreground">
                                                {isChapCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                              </div>
                                            </div>
                                          </div>

                                          {!isChapCollapsed && (
                                            <>
                                              {/* Desktop Table View */}
                                              <div className="hidden md:block overflow-x-auto">
                                                <table className="w-full text-xs text-left">
                                                  <thead className="bg-muted/30 border-b border-border/40 text-muted-foreground uppercase text-[9px] font-bold tracking-wider">
                                                    <tr>
                                                      <th className="py-2 px-3 w-10 text-center">#</th>
                                                      <th className="py-2 px-3">Question Preview</th>
                                                      <th className="py-2 px-3 text-center w-20">Type</th>
                                                      <th className="py-2 px-3 text-center w-20">Difficulty</th>
                                                      <th className="py-2 px-3 text-right w-10">Action</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-border/40">
                                                    {chapQs.map(q => (
                                                      <tr key={q.id} className="hover:bg-muted/20 transition-colors">
                                                        <td className="py-2 px-3 font-bold text-muted-foreground text-center">{q.order_index}</td>
                                                        <td className="py-2 px-3">
                                                          <Link
                                                            href={`/admin/questions/${q.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}
                                                            className="group/link inline-flex items-start gap-1.5 hover:text-primary transition-colors text-[11px] font-medium"
                                                          >
                                                            <KatexRenderer content={q.preview || 'No preview available'} className="line-clamp-2" />
                                                            <ExternalLink className="h-3 w-3 text-muted-foreground group-hover/link:text-primary shrink-0 mt-0.5 opacity-60 group-hover/link:opacity-100" />
                                                          </Link>
                                                        </td>
                                                        <td className="py-2 px-3 text-center">
                                                          <Badge variant="secondary" className="text-[9px] font-mono uppercase">{q.question_type}</Badge>
                                                        </td>
                                                        <td className="py-2 px-3 text-center">
                                                          <Badge variant="outline" className="text-[9px]">{q.difficulty}</Badge>
                                                        </td>
                                                        <td className="py-2 px-3 text-right">
                                                          {test.status === 'draft' ? (
                                                            <Button
                                                              size="icon"
                                                              variant="ghost"
                                                              className="h-6 w-6 text-destructive hover:bg-destructive/10"
                                                              onClick={() => handleRemoveQuestion(q.id)}
                                                              title="Remove question"
                                                            >
                                                              <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                          ) : (
                                                            <span className="text-[10px] text-muted-foreground italic">Locked</span>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>

                                              {/* Mobile Cards View */}
                                              <div className="md:hidden divide-y divide-border/40">
                                                {chapQs.map(q => (
                                                  <div key={q.id} className="p-3 space-y-2 bg-card">
                                                    <Link
                                                      href={`/admin/questions/${q.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}
                                                      className="group/link inline-flex items-start gap-1.5 hover:text-primary transition-colors text-xs font-medium text-foreground leading-snug break-words"
                                                    >
                                                      <KatexRenderer content={q.preview || 'No preview available'} className="line-clamp-3" />
                                                      <ExternalLink className="h-3 w-3 text-muted-foreground group-hover/link:text-primary shrink-0 mt-0.5 opacity-60" />
                                                    </Link>
                                                    <div className="flex items-center justify-between pt-1 border-t border-border/30 text-[10px]">
                                                      <div className="flex items-center gap-1.5 font-mono">
                                                        <span className="font-bold text-muted-foreground">#{q.order_index}</span>
                                                        <Badge variant="secondary" className="text-[9px] uppercase px-1.5 py-0 h-4">{q.question_type}</Badge>
                                                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">{q.difficulty}</Badge>
                                                      </div>
                                                      {test.status === 'draft' ? (
                                                        <Button
                                                          size="icon"
                                                          variant="ghost"
                                                          className="h-6 w-6 text-destructive hover:bg-destructive/10"
                                                          onClick={() => handleRemoveQuestion(q.id)}
                                                          title="Remove question"
                                                        >
                                                          <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                      ) : (
                                                        <span className="text-[10px] text-muted-foreground italic">Locked</span>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : groupMode === 'subject' ? (
              <div className="space-y-4">
                {Array.from(new Set(questions.map(q => q.subject?.name || 'General Subject'))).map(subjName => {
                  const subQs = questions.filter(q => (q.subject?.name || 'General Subject') === subjName)
                  const isSubCollapsed = collapsedPrimaries[subjName]
                  const chapNames = Array.from(new Set(subQs.map(q => q.chapter?.name || 'General Topics')))

                  return (
                    <div
                      key={subjName}
                      className="border border-border/80 rounded-2xl bg-card shadow-glass-xs overflow-hidden transition-all"
                    >
                      <div
                        onClick={() => togglePrimary(subjName)}
                        className="p-3.5 bg-muted/40 hover:bg-muted/60 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 border-b border-border/60 transition-colors select-none"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-7 w-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-foreground block">
                              {subjName}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {chapNames.length} {chapNames.length === 1 ? 'Chapter' : 'Chapters'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-2">
                          <Badge variant="outline" className="text-xs font-bold bg-primary/10 text-primary border-primary/25 px-2.5 py-0.5 rounded-lg flex items-center gap-1.5">
                            <span>{subQs.length} Questions</span>
                          </Badge>
                          <div className="text-muted-foreground">
                            {isSubCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                        </div>
                      </div>

                      {!isSubCollapsed && (
                        <div className="p-3 sm:p-3.5 space-y-3 bg-card">
                          {chapNames.map(chapName => {
                            const chapQs = subQs.filter(q => (q.chapter?.name || 'General Topics') === chapName)
                            const chapKey = `${subjName}-${chapName}`
                            const isChapCollapsed = collapsedChapters[chapKey]

                            return (
                              <div key={chapName} className="border border-border/70 rounded-xl overflow-hidden bg-background">
                                <div
                                  onClick={() => toggleChapter(chapKey)}
                                  className="px-3.5 py-2.5 bg-muted/20 hover:bg-muted/40 cursor-pointer flex items-center justify-between gap-2 border-b border-border/40 transition-colors select-none"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Folder className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="text-xs font-semibold text-foreground truncate">
                                      {chapName}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <Badge variant="outline" className="text-[10px] font-semibold bg-muted text-foreground border-border px-2 py-0 h-4 flex items-center gap-1">
                                      <span>{chapQs.length} Questions</span>
                                    </Badge>
                                    <div className="text-muted-foreground">
                                      {isChapCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                    </div>
                                  </div>
                                </div>

                                {!isChapCollapsed && (
                                  <>
                                    {/* Desktop Table View */}
                                    <div className="hidden md:block overflow-x-auto">
                                      <table className="w-full text-xs text-left">
                                        <thead className="bg-muted/30 border-b border-border/40 text-muted-foreground uppercase text-[9px] font-bold tracking-wider">
                                          <tr>
                                            <th className="py-2 px-3 w-10 text-center">#</th>
                                            <th className="py-2 px-3">Question Preview</th>
                                            <th className="py-2 px-3 text-center w-20">Type</th>
                                            <th className="py-2 px-3 text-center w-20">Difficulty</th>
                                            <th className="py-2 px-3 text-right w-10">Action</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/40">
                                          {chapQs.map(q => (
                                            <tr key={q.id} className="hover:bg-muted/20 transition-colors">
                                              <td className="py-2 px-3 font-bold text-muted-foreground text-center">{q.order_index}</td>
                                              <td className="py-2 px-3">
                                                <Link
                                                  href={`/admin/questions/${q.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}
                                                  className="group/link inline-flex items-start gap-1.5 hover:text-primary transition-colors text-[11px] font-medium"
                                                >
                                                  <KatexRenderer content={q.preview || 'No preview available'} className="line-clamp-2" />
                                                  <ExternalLink className="h-3 w-3 text-muted-foreground group-hover/link:text-primary shrink-0 mt-0.5 opacity-60 group-hover/link:opacity-100" />
                                                </Link>
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                <Badge variant="secondary" className="text-[9px] font-mono uppercase">{q.question_type}</Badge>
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                <Badge variant="outline" className="text-[9px]">{q.difficulty}</Badge>
                                              </td>
                                              <td className="py-2 px-3 text-right">
                                                {test.status === 'draft' ? (
                                                  <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-6 w-6 text-destructive hover:bg-destructive/10"
                                                    onClick={() => handleRemoveQuestion(q.id)}
                                                    title="Remove question"
                                                  >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                  </Button>
                                                ) : (
                                                  <span className="text-[10px] text-muted-foreground italic">Locked</span>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    {/* Mobile Cards View */}
                                    <div className="md:hidden divide-y divide-border/40">
                                      {chapQs.map(q => (
                                        <div key={q.id} className="p-3 space-y-2 bg-card">
                                          <Link
                                            href={`/admin/questions/${q.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}
                                            className="group/link inline-flex items-start gap-1.5 hover:text-primary transition-colors text-xs font-medium text-foreground leading-snug break-words"
                                          >
                                            <KatexRenderer content={q.preview || 'No preview available'} className="line-clamp-3" />
                                            <ExternalLink className="h-3 w-3 text-muted-foreground group-hover/link:text-primary shrink-0 mt-0.5 opacity-60" />
                                          </Link>
                                          <div className="flex items-center justify-between pt-1 border-t border-border/30 text-[10px]">
                                            <div className="flex items-center gap-1.5 font-mono">
                                              <span className="font-bold text-muted-foreground">#{q.order_index}</span>
                                              <Badge variant="secondary" className="text-[9px] uppercase px-1.5 py-0 h-4">{q.question_type}</Badge>
                                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">{q.difficulty}</Badge>
                                            </div>
                                            {test.status === 'draft' ? (
                                              <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-6 w-6 text-destructive hover:bg-destructive/10"
                                                onClick={() => handleRemoveQuestion(q.id)}
                                                title="Remove question"
                                              >
                                                <Trash2 className="h-3.5 w-3.5" />
                                              </Button>
                                            ) : (
                                              <span className="text-[10px] text-muted-foreground italic">Locked</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <>
                {/* Desktop Flat Table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10 text-center">#</TableHead>
                        <TableHead>Question & Taxonomy</TableHead>
                        <TableHead className="w-20">Type</TableHead>
                        <TableHead className="w-24">Difficulty</TableHead>
                        <TableHead className="w-14 text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {questions.map(q => (
                        <TableRow key={q.id} className="align-top">
                          <TableCell className="font-semibold text-xs text-center pt-3.5">{q.order_index}</TableCell>
                          <TableCell className="space-y-2 py-3">
                            <div>
                              <Link
                                href={`/admin/questions/${q.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}
                                className="group inline-flex items-start gap-1.5 text-foreground hover:text-primary transition-colors text-sm font-medium leading-relaxed"
                              >
                                <KatexRenderer content={q.preview || 'No preview text available'} className="group-hover:underline line-clamp-2" />
                                <ExternalLink className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100 shrink-0 mt-0.5 text-muted-foreground group-hover:text-primary" />
                              </Link>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                              {q.section_name && (
                                <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">
                                  {q.section_name}
                                </Badge>
                              )}
                              {q.subject && (
                                <Badge variant="outline" className="text-[10px] bg-muted/30 border-border/70 text-foreground gap-1">
                                  <BookOpen className="h-3 w-3 text-primary" />
                                  <span>{q.subject.name}</span>
                                </Badge>
                              )}
                              {q.chapter && (
                                <Badge variant="outline" className="text-[10px] bg-muted/30 border-border/70 text-foreground gap-1 max-w-[200px]">
                                  <span className="truncate">{q.chapter.name}</span>
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="pt-3.5"><Badge variant="secondary" className="text-xs font-mono uppercase">{q.question_type}</Badge></TableCell>
                          <TableCell className="pt-3.5"><Badge variant="outline" className="text-xs">{q.difficulty}</Badge></TableCell>
                          <TableCell className="text-right pt-3">
                            {test.status === 'draft' ? (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveQuestion(q.id)}
                                title="Remove question from test"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : (
                              <span className="text-[11px] text-muted-foreground italic px-1">Locked</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Flat Cards */}
                <div className="md:hidden divide-y divide-border/40">
                  {questions.map(q => (
                    <div key={q.id} className="p-3.5 space-y-2 bg-card">
                      <Link
                        href={`/admin/questions/${q.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}
                        className="group inline-flex items-start gap-1.5 text-foreground hover:text-primary transition-colors text-xs font-medium leading-snug break-words"
                      >
                        <KatexRenderer content={q.preview || 'No preview text available'} className="line-clamp-3" />
                        <ExternalLink className="h-3 w-3 opacity-60 shrink-0 mt-0.5 text-muted-foreground group-hover:text-primary" />
                      </Link>

                      <div className="flex flex-wrap items-center gap-1 pt-0.5">
                        {q.section_name && (
                          <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary border-primary/30">
                            {q.section_name}
                          </Badge>
                        )}
                        {q.subject && (
                          <Badge variant="outline" className="text-[9px] bg-muted/30 border-border/70 text-foreground">
                            {q.subject.name}
                          </Badge>
                        )}
                        {q.chapter && (
                          <Badge variant="outline" className="text-[9px] bg-muted/30 border-border/70 text-foreground">
                            {q.chapter.name}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-1.5 border-t border-border/30 text-[10px]">
                        <div className="flex items-center gap-1.5 font-mono">
                          <span className="font-bold text-muted-foreground">#{q.order_index}</span>
                          <Badge variant="secondary" className="text-[9px] uppercase px-1.5 py-0 h-4">{q.question_type}</Badge>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">{q.difficulty}</Badge>
                        </div>
                        {test.status === 'draft' ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-destructive hover:bg-destructive/10"
                            onClick={() => handleRemoveQuestion(q.id)}
                            title="Remove question from test"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">Locked</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

            {/* Syllabus Coverage Report */}
            {test.syllabus_coverage && (
              <Card className="border shadow-sm rounded-xl">
                <CardHeader className="px-5 py-4 border-b">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    Syllabus Coverage
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Sections', items: (test.syllabus_coverage.sections || []).map(s => s.name) },
                    { label: 'Subjects', items: (test.syllabus_coverage.subjects || []).map(s => s.name) },
                    { label: 'Chapters', items: (test.syllabus_coverage.chapters || []).map(c => c.name) },
                    { label: 'Topics',   items: test.syllabus_coverage.topics || [] },
                  ].map(group => {
                    const isExpanded = !!expandedCoverageGroups[group.label]
                    const maxPreview = 5
                    const hasMore = group.items.length > maxPreview
                    const visibleItems = !isExpanded && hasMore ? group.items.slice(0, maxPreview) : group.items
                    const remainingCount = group.items.length - maxPreview

                    return (
                      <div key={group.label}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            {group.label} ({group.items.length})
                          </p>
                          {hasMore && (
                            <button
                              type="button"
                              onClick={() => setExpandedCoverageGroups(prev => ({ ...prev, [group.label]: !prev[group.label] }))}
                              className="text-[11px] font-semibold text-primary hover:underline"
                            >
                              {isExpanded ? 'Show less' : `+${remainingCount} more`}
                            </button>
                          )}
                        </div>
                        {group.items.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic">None detected</p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {visibleItems.map((item, i) => (
                              <span key={i} className="text-xs bg-muted text-foreground px-2 py-0.5 rounded-full border border-border">
                                {item}
                              </span>
                            ))}
                            {!isExpanded && hasMore && (
                              <button
                                type="button"
                                onClick={() => setExpandedCoverageGroups(prev => ({ ...prev, [group.label]: true }))}
                                className="text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-colors px-2.5 py-0.5 rounded-full border border-primary/30 font-medium"
                              >
                                View all {group.items.length} {group.label.toLowerCase()} →
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Sidebar Column */}
          <div className="space-y-6">
            {/* 1. Test Configuration Card (TOP FIRST) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center justify-between">
                  <span>Test Configuration</span>
                  {test.status === 'draft' ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent"
                      onClick={() => setIsEditModalOpen(true)}
                      title="Edit Draft Test Info"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span title="Published test configuration locked">
                      <Settings className="h-4 w-4 text-muted-foreground opacity-50" />
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 text-xs">
                <div className="flex justify-between border-b pb-1.5">
                  <span className="text-muted-foreground">Exam:</span>
                  <span className="font-medium">{test.exam || 'General / Unlinked'}</span>
                </div>
                <div className="flex justify-between border-b pb-1.5">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium capitalize">{test.test_type.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between border-b pb-1.5">
                  <span className="text-muted-foreground">Negative Marking:</span>
                  <span className="font-medium">{test.negative_marking_enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div className="flex justify-between border-b pb-1.5">
                  <span className="text-muted-foreground">Multiple Attempts:</span>
                  <span className="font-medium">{test.allow_multiple_attempts ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created By:</span>
                  <span className="font-medium">{test.created_by || 'System'}</span>
                </div>
              </CardContent>
            </Card>

            {/* 2. Taxonomy & Mapping Context Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <CardTitle className="text-base font-semibold">Taxonomy & Mappings</CardTitle>
                  {test.activated_at && (
                    <Badge variant="outline" className="gap-1 text-[11px] font-normal self-start sm:self-auto">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span>{test.activated_at}</span>
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* Exams */}
                {test.mapped_exams && test.mapped_exams.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Exams</span>
                    <div className="flex flex-col gap-1.5">
                      {test.mapped_exams.map((ex) => (
                        <Button key={ex.id} variant="outline" size="sm" className="h-auto py-1.5 px-2.5 text-xs gap-2 justify-between w-full text-left font-normal" asChild>
                          <Link href={`/admin/exams/${ex.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <GraduationCap className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="break-words leading-tight whitespace-normal">{ex.name}</span>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subjects */}
                {test.mapped_subjects && test.mapped_subjects.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Subjects ({test.mapped_subjects.length})</span>
                    <div className="flex flex-col gap-1.5">
                      {test.mapped_subjects.map((s) => (
                        <Button key={s.id} variant="outline" size="sm" className="h-auto py-1.5 px-2.5 text-xs gap-2 justify-between w-full text-left font-normal" asChild>
                          <Link href={`/admin/subjects/${s.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <BookOpen className="h-3.5 w-3.5 text-secondary shrink-0" />
                              <span className="break-words leading-tight whitespace-normal">{s.name}</span>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chapters */}
                {test.mapped_chapters && test.mapped_chapters.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Chapters ({test.mapped_chapters.length})</span>
                    <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto pt-0.5">
                      {test.mapped_chapters.map((c) => (
                        <Button key={c.id} variant="outline" size="sm" className="h-auto py-1.5 px-2.5 text-xs gap-2 justify-between w-full text-left font-normal" asChild>
                          <Link href={c.subject_id ? `/admin/subjects/${c.subject_id}?return_to=${encodeURIComponent(currentPathWithSearch)}` : `/admin/chapters/${c.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <BookOpen className="h-3.5 w-3.5 text-tertiary shrink-0" />
                              <span className="break-words leading-tight whitespace-normal">{c.name}</span>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sections */}
                {test.mapped_sections && test.mapped_sections.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Sections ({test.mapped_sections.length})</span>
                    <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto pt-0.5">
                      {test.mapped_sections.map((sec) => (
                        <Button key={sec.id} variant="outline" size="sm" className="h-auto py-1.5 px-2.5 text-xs gap-2 justify-between w-full text-left font-normal" asChild>
                          <Link href={sec.subject_id ? `/admin/subjects/${sec.subject_id}?return_to=${encodeURIComponent(currentPathWithSearch)}` : `/admin/sections/${sec.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Layers className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="break-words leading-tight whitespace-normal">{sec.name}</span>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Standards */}
                {test.mapped_standards && test.mapped_standards.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Standards ({test.mapped_standards.length})</span>
                    <div className="flex flex-col gap-1.5">
                      {test.mapped_standards.map((st) => (
                        <Button key={st.id} variant="outline" size="sm" className="h-auto py-1.5 px-2.5 text-xs gap-2 justify-between w-full text-left font-normal" asChild>
                          <Link href={`/admin/standards/${st.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Layers className="h-3.5 w-3.5 text-secondary shrink-0" />
                              <span className="break-words leading-tight whitespace-normal">{st.name}</span>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Syllabi */}
                {test.mapped_syllabi && test.mapped_syllabi.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1.5 font-medium">Mapped Syllabi ({test.mapped_syllabi.length})</span>
                    <div className="flex flex-col gap-1.5">
                      {test.mapped_syllabi.map((sy) => (
                        <Button key={sy.id} variant="outline" size="sm" className="h-auto py-1.5 px-2.5 text-xs gap-2 justify-between w-full text-left font-normal" asChild>
                          <Link href={`/admin/syllabi/${sy.id}?return_to=${encodeURIComponent(currentPathWithSearch)}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <BookOpen className="h-3.5 w-3.5 text-tertiary shrink-0" />
                              <span className="break-words leading-tight whitespace-normal">{sy.name}</span>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 3. Student Performance Analytics Card (Only shown when published or when student attempts exist) */}
            {test.status === 'published' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center justify-between">
                    <span>Student Performance Analytics</span>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {/* Score Accuracy Bar */}
                  <div className="space-y-1.5 bg-muted/40 p-3 rounded-lg border border-border">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-medium text-muted-foreground">Class Average Percentage</span>
                      <span className="font-bold text-foreground">{test.average_percentage ?? 0}%</span>
                    </div>
                    <Progress value={test.average_percentage || 0} className="h-2" />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Avg score: <span className="font-semibold text-foreground">{test.average_score ?? 0}</span> / {test.total_marks ?? 0} pts
                    </p>
                  </div>

                  {/* Score Stats Grid */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 border border-border rounded-md bg-card shadow-2xs">
                      <Trophy className="h-3.5 w-3.5 text-primary mx-auto mb-1" />
                      <div className="text-[10px] text-muted-foreground">Highest</div>
                      <div className="font-bold text-sm text-foreground">{test.highest_score ?? 0}</div>
                    </div>
                    <div className="p-2 border border-border rounded-md bg-card shadow-2xs">
                      <BarChart3 className="h-3.5 w-3.5 text-secondary mx-auto mb-1" />
                      <div className="text-[10px] text-muted-foreground">Average</div>
                      <div className="font-bold text-sm text-foreground">{test.average_score ?? 0}</div>
                    </div>
                    <div className="p-2 border border-border rounded-md bg-card shadow-2xs">
                      <TrendingDown className="h-3.5 w-3.5 text-destructive mx-auto mb-1" />
                      <div className="text-[10px] text-muted-foreground">Lowest</div>
                      <div className="font-bold text-sm text-foreground">{test.lowest_score ?? 0}</div>
                    </div>
                  </div>

                  {/* Time Analytics Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 border border-border rounded-md bg-card shadow-2xs">
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                        <Timer className="h-3 w-3 text-primary" /> Avg Time
                      </div>
                      <div className="font-semibold text-sm">{formatDuration(test.avg_time_spent_seconds || 0)}</div>
                    </div>
                    <div className="p-2.5 border border-border rounded-md bg-card shadow-2xs">
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                        <Hourglass className="h-3 w-3 text-secondary" /> Total Time
                      </div>
                      <div className="font-semibold text-sm">{formatDuration(test.total_time_spent_seconds || 0)}</div>
                    </div>
                  </div>

                  {/* Attempts & Revenue Counters */}
                  <div className="pt-2 border-t border-border space-y-2">
                    {(test.attempts_count || 0) > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" /> Total Attempts
                        </span>
                        <span className="font-semibold">{test.attempts_count} ({test.unique_users_count} Users)</span>
                      </div>
                    )}

                    {(test.total_credits_generated || 0) > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Coins className="h-3.5 w-3.5 text-primary" /> Credits Generated
                        </span>
                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                          {test.total_credits_generated} Credits
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Edit Test Modal Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Test Configuration</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-title">Test Title</Label>
              <Input
                id="edit-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                placeholder="e.g. Full Mock Test CET 1"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-desc">Description</Label>
              <Textarea
                id="edit-desc"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="Enter test instructions or description..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Target Exam</Label>
                <Select
                  value={editFormData.exam_id}
                  onValueChange={(val) => setEditFormData({ ...editFormData, exam_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Exam" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General / Unlinked</SelectItem>
                    {available_exams.map((ex) => (
                      <SelectItem key={ex.id} value={String(ex.id)}>
                        {ex.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Language</Label>
                <Select
                  value={editFormData.language}
                  onValueChange={(val) => setEditFormData({ ...editFormData, language: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English (EN)</SelectItem>
                    <SelectItem value="hi">Hindi (HI)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-duration">Duration (minutes)</Label>
                <Input
                  id="edit-duration"
                  type="number"
                  min="1"
                  value={editFormData.duration_minutes}
                  onChange={(e) => setEditFormData({ ...editFormData, duration_minutes: parseInputVal(e.target.value) })}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Access Type</Label>
                <Select
                  value={editFormData.is_free ? 'free' : 'paid'}
                  onValueChange={(val) => setEditFormData({ ...editFormData, is_free: val === 'free' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free Access</SelectItem>
                    <SelectItem value="paid">Paid (Credits)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!editFormData.is_free && (
              <div className="space-y-1.5">
                <Label htmlFor="edit-credit">Credit Cost</Label>
                <Input
                  id="edit-credit"
                  type="number"
                  min="0"
                  value={editFormData.credit_cost}
                  onChange={(e) => setEditFormData({ ...editFormData, credit_cost: parseInputVal(e.target.value) })}
                />
              </div>
            )}

            <div className="pt-2 border-t space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-semibold">Negative Marking</Label>
                  <p className="text-[11px] text-muted-foreground">Apply negative marking rules to questions</p>
                </div>
                <Switch
                  checked={editFormData.negative_marking_enabled}
                  onCheckedChange={(chk) => setEditFormData({ ...editFormData, negative_marking_enabled: chk })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-semibold">Allow Multiple Attempts</Label>
                  <p className="text-[11px] text-muted-foreground">Permit students to re-take this test multiple times</p>
                </div>
                <Switch
                  checked={editFormData.allow_multiple_attempts}
                  onCheckedChange={(chk) => setEditFormData({ ...editFormData, allow_multiple_attempts: chk })}
                />
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingEdit}>
                {isSubmittingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  )
}
